import { Dropdown, Select, Spin } from '@surecompanies/core_components'
import { DownOutlined } from '@ant-design/icons'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getWaiter } from 'redux-waiter'

// hixme-ui
import Button from '@hixme-ui/button'
import Container from '@hixme-ui/container'
import Icon from '@hixme-ui/icon'
import Text from '@hixme-ui/text'
import { colors } from '@hixme-ui/theme'

import { getVirtualCardsForEmployee } from 'modules/virtual-cards/actions'
import { VCN_POST } from 'modules/virtual-cards/constants'
import { providerOptionLabel } from 'modules/virtual-cards/useVcnProviderAvailability'
import { personByIdSelector } from 'store/modules/persons/selectors'

import GenerateVcnModalLegacy from './GenerateVcnModalLegacy'
import useVcnActionsLegacy from './useVcnActionsLegacy'

/** A provider switched off by its safety flag drops out of the menu entirely. */
const buildProviderMenuItems = ({ enabledProviders }) =>
  enabledProviders.map((provider) => ({
    key: provider,
    label: providerOptionLabel(provider, enabledProviders),
  }))

/**
 * VCN card selector component displayed in the VirtualCard section.
 * Renders a dropdown to select from available VCNs.
 */
const VcnCardSelector = ({ availableCards, currentHealthPlanId, onSelect }) => (
  <Select
    style={{ width: 200 }}
    placeholder="Select VCN"
    optionLabelProp="label"
    onChange={onSelect}
    dropdownMatchSelectWidth={false}
    dropdownRender={(menu) => (
      <Container style={{ padding: '4px 4px 12px' }}>
        <Container noPadding flex direction="row" style={{ marginBottom: 4 }}>
          <Text small bolder lighter style={{ width: 176, padding: '0px 8px' }}>
            VCN
          </Text>
          <Text lighter small bolder style={{ width: 76, padding: '0px 8px' }}>
            HIOS
          </Text>
          <Text lighter small bolder style={{ width: 144, padding: '0px 8px' }}>
            Member
          </Text>
          <Text lighter small bolder style={{ width: 144, padding: '0px 8px' }}>
            Current Carrier
          </Text>
          <Text lighter small bolder style={{ width: 144, padding: '0px 8px' }}>
            VCN Status
          </Text>
          <Text lighter small bolder style={{ width: 144, padding: '0px 8px' }}>
            VCN Provider
          </Text>
        </Container>
        {menu}
      </Container>
    )}
  >
    {availableCards.map((opt, index) => (
      <Select.Option
        style={{ margin: 0, padding: 0, minHeight: 'unset' }}
        key={index}
        label={opt.card}
        value={index}
      >
        <Container
          noPadding
          flex
          direction="row"
          alignItems="center"
          style={{ height: '100%' }}
        >
          <Container
            flex
            noPadding
            direcion="column"
            justifyContents="center"
            style={{
              width: 176,
              padding: '4px 8px',
              borderRight: `1px solid ${colors.grey120}`,
            }}
          >
            <Text muted>{opt.card}</Text>
          </Container>
          <Container
            flex
            noPadding
            direcion="column"
            justifyContents="center"
            style={{
              width: 76,
              padding: '4px 8px',
              borderRight: `1px solid ${colors.grey120}`,
            }}
          >
            <Text muted>{opt.HIOS.substr(-5)}</Text>
          </Container>
          <Container
            flex
            noPadding
            direcion="column"
            justifyContents="center"
            style={{ width: 144, padding: '4px 8px' }}
          >
            <Text muted>{opt.name}</Text>
          </Container>
          <Container
            flex
            noPadding
            direcion="column"
            justifyContents="center"
            style={{
              width: 144,
              padding: '4px 8px',
              borderRight: `1px solid ${colors.grey120}`,
            }}
          >
            <Text muted>{opt.HIOS === currentHealthPlanId ? 'YES' : 'NO'}</Text>
          </Container>
          <Container
            flex
            noPadding
            direcion="column"
            justifyContents="center"
            style={{
              width: 144,
              padding: '4px 8px',
              borderRight: `1px solid ${colors.grey120}`,
            }}
          >
            <Text muted>{opt.status}</Text>
          </Container>
          <Container
            flex
            noPadding
            direcion="column"
            justifyContents="center"
            style={{
              width: 144,
              padding: '4px 8px',
              borderRight: `1px solid ${colors.grey120}`,
            }}
          >
            <Text muted>{opt.virtualCardType}</Text>
          </Container>
        </Container>
      </Select.Option>
    ))}
  </Select>
)

VcnCardSelector.propTypes = {
  availableCards: PropTypes.array.isRequired,
  currentHealthPlanId: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
}

/**
 * NoCard component - displays VCN selection and generation controls
 * when no active card is present or for managing VCN operations.
 */
const NoCard = ({
  benefitId,
  employeePublicKey,
  onGenerateCard,
  isCreateAvailable,
  currentHealthPlanId,
  currentCard,
}) => {
  const dispatch = useDispatch()
  const [selectedCard, setSelectedCard] = useState(null)
  const [isRelatedCardsLoading, setIsRelatedCardsLoading] = useState(true)

  const availableCards = useSelector(
    (state) => state.virtualCards.employeeFamilyVirtualCards
  )
  const person = useSelector((state) =>
    personByIdSelector(state)(employeePublicKey)
  )
  const isGenerating = useSelector(
    (state) => getWaiter(state, `${VCN_POST}-${benefitId}`).isPending || false
  )

  // Use custom hook for VCN actions
  const {
    enableNoteModalBeforeCreate,
    enableTerminateAllBeforeCreate,
    generateModalOpen,
    isTerminatingAll,
    shouldShowTerminateWarning,
    providerAvailability,
    handleProviderSelect,
    handleGenerateConfirm,
    handleGenerateCancel,
    handleReuseClick,
  } = useVcnActionsLegacy({
    benefitId,
    employeePublicKey,
    person,
    availableCards,
    selectedCard,
    onGenerateCard,
    currentCard,
  })

  const providerMenuItems = buildProviderMenuItems(providerAvailability)

  // Both providers switched off — nothing left to create with (EMS-9720).
  // Also disable when terminate-all is enabled but cards are still loading
  // to prevent skipping cleanup
  const isCardsDataLoading = isRelatedCardsLoading
  const isGenerateDisabledForTerminateAll =
    enableTerminateAllBeforeCreate && isCardsDataLoading

  const isGenerateEnabled =
    isCreateAvailable &&
    !isGenerating &&
    providerAvailability.hasAnyProvider &&
    !isGenerateDisabledForTerminateAll

  // Load available cards on mount
  useEffect(() => {
    dispatch(getVirtualCardsForEmployee(employeePublicKey)).finally(() => {
      setIsRelatedCardsLoading(false)
    })
  }, [dispatch, employeePublicKey])

  const handleVcnSelect = (selectIndex) => {
    setSelectedCard(selectIndex)
  }

  const showAvailableCards =
    !isRelatedCardsLoading &&
    Array.isArray(availableCards) &&
    availableCards.length > 0

  return (
    <>
      <Container
        flex
        padding="10px"
        style={{ opacity: isCreateAvailable ? 1 : '0.4', gap: 8 }}
        alignItems="center"
      >
        <span style={{ marginTop: 8 }}>
          <Icon light creditCard size={2} />
        </span>

        {isRelatedCardsLoading && (
          <Text muted small>
            Retrieving VCNs...
          </Text>
        )}

        {showAvailableCards && (
          <Container
            flex
            direction="row"
            alignItems="center"
            noPadding
            style={{ gap: 4 }}
          >
            <VcnCardSelector
              availableCards={availableCards}
              currentHealthPlanId={currentHealthPlanId}
              onSelect={handleVcnSelect}
              selectedCard={selectedCard}
            />
            {selectedCard !== null && selectedCard >= 0 && (
              <Button mini onClick={handleReuseClick} style={{ minWidth: 88 }}>
                Reuse VCN
              </Button>
            )}
          </Container>
        )}

        <Container
          flex
          noPadding
          alignItems="center"
          style={{ marginLeft: '10px', gap: 8 }}
        >
          <Dropdown
            menu={{ items: providerMenuItems, onClick: handleProviderSelect }}
            trigger={['click']}
            disabled={!isGenerateEnabled}
          >
            <Text
              cursor={isGenerateEnabled ? 'pointer' : 'not-allowed'}
              primary={isGenerateEnabled}
              style={{ opacity: isGenerateEnabled ? 1 : 0.5 }}
            >
              Generate VCN <DownOutlined style={{ fontSize: 10 }} />
            </Text>
          </Dropdown>
          {(isGenerating || isTerminatingAll) && <Spin size="small" />}
        </Container>
      </Container>

      {/* Generate VCN Modal */}
      <GenerateVcnModalLegacy
        open={generateModalOpen}
        onCancel={handleGenerateCancel}
        onConfirm={handleGenerateConfirm}
        loading={isGenerating || isTerminatingAll}
        useActivityLogPopup={enableNoteModalBeforeCreate}
        showTerminateWarning={shouldShowTerminateWarning}
        personFullName={person?.FullName}
      />
    </>
  )
}

NoCard.propTypes = {
  onGenerateCard: PropTypes.func.isRequired,
  isCreateAvailable: PropTypes.bool.isRequired,
  benefitId: PropTypes.string.isRequired,
  employeePublicKey: PropTypes.string,
  currentHealthPlanId: PropTypes.string,
  currentCard: PropTypes.object,
}

NoCard.defaultProps = {
  employeePublicKey: '',
  currentHealthPlanId: '',
  currentCard: null,
}

export default NoCard
